import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/common/PrivateRoute';
import DefaultRedirect from './components/common/DefaultRedirect';
import LoadingSpinner from './components/common/LoadingSpinner';

const LoginPage = lazy(() => import('./pages/common_pages/LoginPage'));
const OperationsPage = lazy(() => import('./pages/operations_page/OperationsPage'));
const SalesPage = lazy(() => import('./pages/sales_page/SalesPage'));
const FinancePage = lazy(() => import('./pages/finance_page/FinancePage'));
const ManagementPage = lazy(() => import('./pages/management_page/ManagementPage'));
const KentekenPage = lazy(() => import('./pages/kenteken_page/KentekenPage'));
const AccessDeniedPage = lazy(() => import('./pages/common_pages/AccessDeniedPage'));
const AdminPage = lazy(() => import('./pages/common_pages/AdminPage'));
const AgentPage = lazy(() => import('./pages/agent_page/AgentPage'));

// Public pages
const LandingPage = lazy(() => import('./pages/public/LandingPage'));
const DashboardPage = lazy(() => import('./pages/dashboard_page/DashboardPage'));
const PortalManagePage = lazy(() => import('./pages/dashboard_page/PortalManagePage'));
const SchadeReviewPage = lazy(() => import('./pages/public/SchadeReviewPage'));

function App() {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    window.gtag('config', 'G-9BSGMF39Y7', {
      page_path: location.pathname,
    });
  }, [location]);

  return (
    <AuthProvider>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public welcome page (old marketing URLs fall through to the catch-all → /) */}
          <Route path="/landing" element={<LandingPage />} />

          {/* Portal dashboard routes (public) */}
          <Route path="/portal/lead" element={<DashboardPage type="lead" />} />
          <Route path="/portal/client" element={<DashboardPage type="client" />} />

          {/* Schade AI review (public — link-based access) */}
          <Route path="/review/:caseId" element={<SchadeReviewPage />} />

          {/* Auth routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route path="/agent" element={<AgentPage />} />

          {/* Protected routes with specific access requirements */}
          <Route element={<PrivateRoute />}>
            <Route path="/operations/*" element={<OperationsPage />} />

            <Route element={<PrivateRoute requiredAccess="sales" />}>
              <Route path="/sales/*" element={<SalesPage />} />
            </Route>

            <Route element={<PrivateRoute requiredAccess="finance" />}>
              <Route path="/finance/*" element={<FinancePage />} />
            </Route>

            <Route element={<PrivateRoute requiredAccess="management" />}>
              <Route path="/management/*" element={<ManagementPage />} />
            </Route>

            {/* Portal management - accessible to all authenticated users */}
            <Route path="/portal/manage" element={<PortalManagePage />} />

            {/* Kenteken route - accessible to all authenticated users */}
            <Route path="/kenteken/:licensePlate" element={<KentekenPage />} />

            {/* Admin route - only admins can access */}
            <Route path="/admin" element={<AdminPage />} />
          </Route>

          {/* Root: landing page for visitors, role-aware default page for authenticated users */}
          <Route
            path="/"
            element={
              <PrivateRoute fallback={<LandingPage />}>
                <DefaultRedirect />
              </PrivateRoute>
            }
          />

          {/* Catch-all: redirect to root */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}

export default function WrappedApp() {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
}
